'use client';

import React, { JSX } from 'react';
import {
  LinkSwitchableMixin,
  MagnoliaComponentNode,
  MagnoliaDamNode,
  customImageSizeSwitchableMixin
} from '@/Util/magnolia';
import styles from './Image.module.scss';
import Link from 'next/link';
import { getNextLinkAttrs, getLinkUrl } from '../linkList/Link';
import ImageWrapper from '@/components/ImageWrapper';
import { DateInfoType } from '../ibe/IbeProps';
import PackageParamsTransformer from '@/Util/ParamsTransformer/PackageParamsTransformer';
import { DestinationItem, PackageSearchParams, useAppService, useConfig } from '@ibe/components';
import { getDatesFromProps } from '@/components/offer/Offer';
import YsnConfigModel from '@/Config/YsnConfigModel';
import { useMount } from 'react-use';
import { useLocalObservable } from 'mobx-react';
import {
  CustomPackageStore,
  fetchDestinationsGeoUnits,
  fetchDestinationsProducts,
  findDestinationsCodeAndIds
} from '../ibe/IbeUtils';
import { ApiGeoUnit, ApiItemType, ApiProductData, ApiSortDirection, ApiSortOption } from '@ibe/api';
import { useApi } from '@/Util/useApi';

interface PaxContainersTypeProps extends Omit<MagnoliaComponentNode, '@nodes'> {
  '@nodes': string[];
  numberOfAdults: string;
  numberOfChildren: string;
  numberOfInfants: string;
}

interface Props extends customImageSizeSwitchableMixin {
  image: MagnoliaDamNode;
  imageAltText?: string;
  imageCaption?: string;
  keepAspectRatio?: boolean;
  imageCredit?: string;
  imageTitle?: string;
  metadata: MagnoliaComponentNode;
  openNewBrowserWindow?: boolean;
  searchInfo?: {
    dateInfo: DateInfoType;
    destinations?: string;
    packageCode?: string;
    duration?: string;
    preSelectedCharacteristics: string;
    type: 'search' | 'link';
    ibeUrl: string;
    preFetch?: boolean;
    paxContainers: PaxContainersTypeProps & { [key: string]: PaxContainersTypeProps };
  } & LinkSwitchableMixin;
}

const Image = (props: Props & customImageSizeSwitchableMixin): JSX.Element => {
  const config = useConfig() as YsnConfigModel;

  const appService = useAppService();
  const api = useApi();

  const store = useLocalObservable(
    () =>
      new CustomPackageStore(
        api,
        config,
        {
          sortDirection: ApiSortDirection.ASC,
          sortOption: ApiSortOption.PRICE
        },
        () => '',
        appService
      )
  );
  const { linkTypedownload, linkTypeexternal, linkTypepage, preFetch } = props.searchInfo ?? {};

  useMount(async () => {
    if (preFetch) {
      const req = [
        fetchDestinationsProducts(api, searchParams.destinations || []),
        fetchDestinationsGeoUnits(api)
      ];
      const [products, geoUnits] = await Promise.all(req);
      searchParams.destinations = findDestinationsCodeAndIds(
        products as ApiProductData[],
        geoUnits as ApiGeoUnit[],
        searchParams
      );
      store.setSearchParams(searchParams);
      await store.loadPackages(ApiItemType.PREDEFINEDPACKAGE, true, true);
    }
  });

  if (!props?.searchInfo) return <></>;
  const {
    searchInfo,
    image,
    imageCaption,
    imageSize,
    keepAspectRatio,
    imageSizecustomImageWidth,
    imageSizecustomImageHeight,
    imageCredit
  } = props;

  const [url, searchParams] =
    searchInfo?.type === 'search' ? getSearchUrl(props, config) : ['', {}];
  let linkAttrs = getNextLinkAttrs(
    {
      ...props?.searchInfo,
      openNewBrowserWindow: props?.openNewBrowserWindow
    },
    config
  );
  if (searchInfo?.type === 'search' && url) {
    linkAttrs = { ...linkAttrs, href: url };
  }

  return (
    <figure className={styles.Image}>
      {(linkAttrs?.href &&
        (searchInfo?.type === 'search' || linkTypedownload || linkTypeexternal || linkTypepage) && (
          <Link {...linkAttrs} locale={linkAttrs.download === true ? false : undefined}>
            <ImageWrapper
              className={imageSize}
              style={keepAspectRatio ? { objectFit: 'contain' } : {}}
              image={image}
              width={imageSize === 'custom' ? imageSizecustomImageWidth : image?.metadata?.width}
              height={imageSize === 'custom' ? imageSizecustomImageHeight : image?.metadata?.height}
            />
          </Link>
        )) || (
        <ImageWrapper
          className={imageSize}
          style={keepAspectRatio ? { objectFit: 'contain' } : {}}
          image={image}
          width={imageSize === 'custom' ? imageSizecustomImageWidth : image?.metadata?.width}
          height={imageSize === 'custom' ? imageSizecustomImageHeight : image?.metadata?.height}
        />
      )}
      {(imageCaption || imageCredit) && (
        <figcaption className={styles.Image__caption}>
          <span>{imageCaption ?? ''}</span>
          {imageCredit && <span>{imageCredit}</span>}
        </figcaption>
      )}
    </figure>
  );
};

const getSearchUrl = (
  { searchInfo }: Props,
  config: YsnConfigModel
): [string, PackageSearchParams] => {
  let url = '';
  let searchParams: PackageSearchParams = {};
  if (searchInfo) {
    const { destinations, packageCode, duration } = searchInfo;
    const [startDate, endDate] = getDatesFromProps(searchInfo, config);
    const { paxContainers, preSelectedCharacteristics } = searchInfo;
    const params: PackageSearchParams = {
      destinations: (destinations ?? '')
        .split(',')
        .filter(dest => dest)
        .flatMap(dest => ({ code: dest }) as DestinationItem),
      endDate,
      startDate,
      duration: parseInt(duration ?? '7'),
      packageCode,
      occupancy: (paxContainers?.['@nodes'] || []).flatMap(paxContainer => {
        const numberOfAdults = paxContainers[paxContainer].numberOfAdults;
        const numberOfChildren = paxContainers[paxContainer].numberOfChildren;
        const numberOfInfants = paxContainers[paxContainer].numberOfInfants;
        const children = numberOfChildren ? parseInt(numberOfChildren) : 0;
        const infants = numberOfInfants ? parseInt(numberOfInfants) : 0;

        const infantsAges: number[] = numberOfInfants
          ? Array(parseInt(numberOfInfants)).fill(config.traveler.maxInfantAge - 1)
          : [];
        const childrenAges: number[] = children
          ? Array(children).fill(config.traveler.maxChildAge - 1)
          : [];

        return {
          adults: numberOfAdults ? parseInt(numberOfAdults) : 2,
          children: children + infants,
          childrenAges: childrenAges.concat(infantsAges),
          infants: 0,
          infantsAges: []
        };
      }),
      preSelectedCharacteristics: (preSelectedCharacteristics || '')
        .split(',')
        .filter(dest => dest),
      goToDetails: true
    };
    const queryParams = PackageParamsTransformer.encode(params);
    url = `${getLinkUrl(searchInfo?.ibeUrl, config)}?${queryParams}`;
    searchParams = params;
  }

  return [url, searchParams];
};

export default Image;
